import React from 'react';

import type { IInputProps } from './Input.types';
import { Icon } from '@ui-library/Icon/Icon';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Container } from '@ui-library/Container/Container';
import { Text } from '@ui-library/Text/Text';
import { validatePassword, getPasswordCriteria, getStrengthColor, getStrengthBgColor } from '../../utils/passwordValidation';

export const Input: React.FC<IInputProps> = ({
  label,
  helperText,
  errorText,
  size = 'md',
  variant = 'default',
  startIcon,
  endIcon,
  onEndIconClick,
  className = '',
  containerClassName = '',
  labelClassName = '',
  required = false,
  disabled = false,
  id,
  showStrength = false,
  ...inputProps
}: IInputProps) => {

  // Size mapping for input padding and text
  const sizeMapper = {
    'sm': {
      input: 'px-bg-v2-sm py-bg-v2-xs text-bg-v2-sm',
      withStartIcon: 'pl-bg-v2-4xl',
      withEndIcon: 'pr-bg-v2-4xl',
      icon: 'w-4 h-4',
      iconPosition: 'left-bg-v2-sm right-bg-v2-sm',
    },
    'md': {
      input: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-md',
      withStartIcon: 'pl-bg-v2-5xl',
      withEndIcon: 'pr-bg-v2-5xl',
      icon: 'w-5 h-5',
      iconPosition: 'left-bg-v2-lg right-bg-v2-lg',
    },
    'lg': {
      input: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-lg',
      withStartIcon: 'pl-bg-v2-6xl',
      withEndIcon: 'pr-bg-v2-6xl',
      icon: 'w-6 h-6',
      iconPosition: 'left-bg-v2-lg right-bg-v2-lg',
    },
  };

  // Variant mapping for border and focus states
  const variantMapper = {
    'default': 'border-2 border-border-secondary text-text-md font-bg-v2-sans font-bg-v2-regular bg-bg-primary text-text-primary placeholder:text-text-placeholder focus:border-border-primary-hover focus:border-2',
    'error': 'border-2 border-border-error text-text-mdfont-bg-v2-sans font-bg-v2-regular bg-bg-primary text-text-primary placeholder:text-text-placeholder focus:border-border-error focus:border-2',
    'success': 'border-2 border-border-brand text-text-md font-bg-v2-sans font-bg-v2-regular bg-bg-primary text-text-primary placeholder:text-text-placeholder focus:border-border-brand focus:border-2',
  };

  // Determine actual variant based on errorText
  const actualVariant = errorText ? 'error' : variant;

  // Generate unique ID if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Build input classes
  const inputClasses = `
    w-full
    ${sizeMapper[size].input}
    ${startIcon ? sizeMapper[size].withStartIcon : ''}
    ${endIcon ? sizeMapper[size].withEndIcon : ''}
    ${variantMapper[actualVariant]}
    rounded-bg-v2-md
    transition-all
    transition-colors
    duration-200
    focus:outline-none
    disabled:bg-bg-disabled
    disabled:text-text-disabled
    disabled:border-border-disabled
    disabled:cursor-not-allowed
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={`flex flex-col gap-bg-v2-sm w-full ${containerClassName}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className={`font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary ${labelClassName}`}
        >
          {label}
        </label>
      )}

      {/* Input Container */}
      <div className="relative w-full">
        {/* Start Icon */}
        {startIcon && (
          <div className={`absolute ${size === 'sm' ? 'left-bg-v2-sm' : 'left-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} flex items-center justify-center text-text-quaternary pointer-events-none`}>
            {startIcon}
          </div>
        )}

        {/* Input Field */}
        <input
          id={inputId}
          disabled={disabled}
          required={required}
          className={inputClasses}
          {...inputProps}
        />

        {/* End Icon or Error Icon */}
        {errorText ? (
          <div className={`absolute ${size === 'sm' ? 'right-bg-v2-sm' : 'right-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} text-text-error-primary pointer-events-none`}>
            <Icon name={IconName.Alert} size="md" />
          </div>
        ) : endIcon ? (
          <div 
            className={`absolute ${size === 'sm' ? 'right-bg-v2-sm' : 'right-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} text-text-quaternary ${onEndIconClick ? 'cursor-pointer hover:text-text-secondary transition-colors' : ''}`}
            onClick={onEndIconClick}
          >
            {endIcon}
          </div>
        ) : null}
      </div>

      {/* Error Text Only */}
      {errorText && (
        <span className="font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-error-primary">
          {errorText}
        </span>
      )}

      {/* Password Strength Indicator */}
      {showStrength && inputProps.value && typeof inputProps.value === 'string' && inputProps.value.length > 0 && (
        <>
          {/* Strength Bar */}
          <div className="flex flex-col gap-bg-v2-sm">
            {(() => {
              const validation = validatePassword(inputProps.value);
              const strengthWidths = { weak: 25, fair: 50, good: 75, strong: 100 };
              return (
                <>
                  <div className="flex items-center justify-between">
                    <Text variant="text-sm" color="secondary" weight="regular" as="span">
                      Password strength:
                    </Text>
                    <Text variant="text-sm" weight="medium" as="span" className={`${getStrengthColor(validation.strength)} capitalize`}>
                      {validation.strength}
                    </Text>
                  </div>
                  <div className="w-full bg-bg-secondary rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${getStrengthBgColor(validation.strength)}`}
                      style={{ width: `${strengthWidths[validation.strength]}%` }}
                    />
                  </div>
                </>
              );
            })()}
          </div>

          {/* Password Requirements Checklist */}
          <Container variant="secondary" padding="lg" className="rounded-bg-v2-md border border-border-secondary">
            <div className="flex flex-col gap-bg-v2-sm">
              <Text variant="text-sm" weight="medium" className={"font-bg-v2-sans"} color="primary" as="h4">
                Password Requirements:
              </Text>
              {(() => {
                const criteria = getPasswordCriteria(inputProps.value);
                const requirements = [
                  { key: 'minLength', text: 'At least 8 characters long', met: criteria.minLength },
                  { key: 'hasLowerCase', text: 'Contains lowercase letters (a-z)', met: criteria.hasLowerCase },
                  { key: 'hasUpperCase', text: 'Contains uppercase letters (A-Z)', met: criteria.hasUpperCase },
                  { key: 'hasNumber', text: 'Contains at least one number (0-9)', met: criteria.hasNumber },
                  { key: 'hasSpecialChar', text: 'Contains a special character (!@#$%^&*)', met: criteria.hasSpecialChar },
                ];

                return (
                  <ul className="flex flex-col gap-bg-v2-xs">
                    {requirements.map((req) => (
                      <li key={req.key} className="flex items-center gap-bg-v2-sm">
                        {req.met ? (
                          <Icon name={IconName.Check} size="sm" variant="success" />
                        ) : (
                          <Icon name={IconName.Close} size="sm" variant="error" />
                        )}
                        <Text
                          variant="text-sm"
                          weight="medium"
                          as="span"
                          color={req.met ? 'success' : 'error'}
                        >
                          {req.text}
                        </Text>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </Container>
        </>
      )}
    </div>
  );
};

